/**
 * Header Ask bar (MPPI-00092).
 *
 * Ask was a menu item — a destination you navigated to. As a bar in the
 * chrome it becomes the way you start a task, which is a materially different
 * product and the reason it was promoted out of the menu in MPPI-00091.
 *
 * Deliberately a *launcher*, not a second search implementation. It hands the
 * query to the existing `/market/ask` page, which owns the NL search, the
 * result rendering and the conversation state. Reimplementing any of that here
 * would give two code paths that answer the same question differently — and
 * the page is already wired to `nl_search.py` via `routes/search.py`.
 *
 * Scope note: MPPI-00093 (every entity, not just properties), MPPI-00094
 * (general chat, with regulated topics referred to official sources) and
 * MPPI-00095 (prompt telemetry) all land *behind* this bar without changing
 * it. The bar submits a question; what can answer it is the other tickets.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SparklesIcon } from "@heroicons/react/24/solid";
import { AskMicButton } from "@/components/ask/AskMicButton";
import { useBrandingStore } from "@/store/brandingStore";

export function HeaderAskBar() {
  const brand = useBrandingStore((s) => s.branding);
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const [speechLang, setSpeechLang] = useState("en-GB");

  // "/" focuses the bar, the convention users already expect from every tool
  // that has one. Ignored while typing in another field, so it cannot steal a
  // slash from a search box or a text area.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== "/") return;
      const el = document.activeElement;
      const tag = el?.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea" || (el as HTMLElement)?.isContentEditable) {
        return;
      }
      e.preventDefault();
      inputRef.current?.focus();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /** Voice runs the search directly — speaking then clicking is not a voice
   *  control. Matches the microphone on the Ask page. */
  function run(query: string) {
    const trimmed = query.trim();
    if (!trimmed) return;
    navigate(`/market/ask?q=${encodeURIComponent(trimmed)}`);
    setQ("");
    inputRef.current?.blur();
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const query = q.trim();
    if (!query) return;
    // Encoded, so a question containing & or # survives the round trip.
    navigate(`/market/ask?q=${encodeURIComponent(query)}`);
    setQ("");
    inputRef.current?.blur();
  }

  return (
    <form
      onSubmit={submit}
      role="search"
      // Hidden below md: the header is tight on mobile and the bar would
      // crowd out the nav toggle. Ask remains reachable at /market/ask.
      className="hidden flex-1 justify-center px-4 md:flex"
    >
      <div className="relative w-full max-w-2xl self-center">
        {/* AI mark (MPPI-00103). Gold rather than the muted grey a search
            glyph would use — this is the platform's AI, and it should read as
            a capability rather than a plain text field. */}
        <SparklesIcon
          className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-accent"
          aria-hidden
        />
        <input
          ref={inputRef}
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Ask a question about the market"
          placeholder={`Ask ${brand.company_name} AI —  2-bed Marina under 2M with good yield`}
          className="w-full rounded-md border border-white/20 bg-white/10 py-2.5 pl-10 pr-12 text-[15px] text-white placeholder:text-white/50 focus:border-accent focus:bg-white/15 focus:outline-none"
        />
        <span className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1 text-white/70">
          <kbd className="pointer-events-none hidden rounded border border-white/20 px-1.5 py-0.5 text-[10px] text-white/50 lg:block">
            /
          </kbd>
          <AskMicButton
            onTranscript={run}
            lang={speechLang}
            onLangChange={setSpeechLang}
            compact
          />
        </span>

        {/* Attribution (MPPI-00103). Says plainly that answers are AI-generated
            and that they come from this platform's own data — so a user knows
            both what produced the answer and what it was drawn from. It is
            also the honest counterpart to MPPI-00094: an answer labelled as AI
            invites the scrutiny an unlabelled one does not. */}
        {/* Was `absolute top-full`, which rendered it BELOW a fixed h-14
            header and straight underneath <main>, so it was invisible
            (MPPI-00151). Now it sits inside the header's own box: the bar is
            a flex column and the header is tall enough to hold both. */}
        <p className="mt-0.5 hidden w-full truncate text-[10px] leading-tight text-white/45 lg:block">
          AI-powered answers from the {brand.company_name} platform · verify
          before advising a client
        </p>
      </div>
    </form>
  );
}
